import { Box, Grid, Button, Typography, Container, InputAdornment, IconButton } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import TextInput from "../UI/TextInput";
import TextInputPassword from "../UI/TextInputPassword";
import { tokens } from "../../store/tokens";
import { useAuthForm } from '../../store/useAuthForm';

const primaryButtonSx = {
  bgcolor: tokens.color.teal,
  color: tokens.color.white,
  fontWeight: 700,
  borderRadius: `${tokens.radius.pill}px`,
  textTransform: "none",
  py: 1,
  "&:hover": { bgcolor: tokens.color.tealDark },
};

const linkButtonSx = {
  color: tokens.color.teal,
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.8rem",
};

const LoginSignUpFormMini = ({
  isLogin,
  setIsLogin,
  loading,
  setLoading,
  setIsSubmitting,
  setIsLogged,
  handleSnackOpen,
}) => {
  const {
    register,
    errors,
    watch,
    reset,
    onSubmit,
    isUserExist,
    isForgotPassword,
    setIsForgotPassword,
    forgotStep,
    setForgotStep,
    instMessage,
    showPassword,
    setShowPassword,
    showNewPassword,
    setShowNewPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    watchedPassword,
  } = useAuthForm({ isLogin, setIsLogin, setLoading, setIsSubmitting, setIsLogged, handleSnackOpen });

  return (
    <Container maxWidth="xs" sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 2, px: 2 }}>
      <Box
        sx={{
          p: { xs: 2, sm: 2.5 },
          width: "100%",
          maxWidth: 380,
          bgcolor: tokens.bg.surface,
          border: `1px solid ${tokens.color.line}`,
          borderRadius: `${tokens.radius.md}px`,
        }}
      >
        {instMessage && (
          <Typography
            sx={{
              fontFamily: tokens.font.body,
              fontSize: "0.8rem",
              fontWeight: 600,
              color: tokens.color.teal,
              textAlign: "center",
              mb: 1.5,
            }}
          >
            {instMessage}
          </Typography>
        )}

        <Box component="form" onSubmit={onSubmit} noValidate>
          {isForgotPassword ? (
            <>
              {forgotStep === 1 && (
                <TextInput
                  register={register}
                  id="identifier"
                  placeholder="Email or phone number"
                  type="text"
                  rules={{ required: "Enter your email or phone number" }}
                  errors={errors.identifier}
                />
              )}

              {forgotStep === 2 && (
                <TextInput
                  register={register}
                  id="otp"
                  placeholder="Enter OTP"
                  type="text"
                  rules={{
                    required: "OTP is required",
                    pattern: { value: /^[0-9]{4,7}$/, message: "Invalid OTP" },
                  }}
                  errors={errors.otp}
                />
              )}

              {forgotStep === 3 && (
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <TextInputPassword
                      register={register}
                      id="newPassword"
                      placeholder="New password"
                      type={showNewPassword ? "text" : "password"}
                      rules={{
                        required: "New password is required",
                        minLength: { value: 6, message: "Minimum 6 characters" },
                      }}
                      errors={errors.newPassword}
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowNewPassword((v) => !v)}>
                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextInputPassword
                      register={register}
                      id="confirmNewPassword"
                      placeholder="Confirm password"
                      type={showConfirmPassword ? "text" : "password"}
                      rules={{
                        required: "Confirm password",
                        validate: (val) => val === watch("newPassword") || "Passwords do not match",
                      }}
                      errors={errors.confirmNewPassword}
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowConfirmPassword((v) => !v)}>
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      }
                    />
                  </Grid>
                </Grid>
              )}

              <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ ...primaryButtonSx, mt: 2 }}>
                {loading
                  ? "Processing..."
                  : forgotStep === 1
                  ? "Verify"
                  : forgotStep === 2
                  ? "Submit OTP"
                  : "Reset password"}
              </Button>

              <Box sx={{ textAlign: "center", mt: 1 }}>
                <Button
                  size="small"
                  sx={linkButtonSx}
                  onClick={() => {
                    setIsForgotPassword(false);
                    setForgotStep(1);
                    reset();
                  }}
                >
                  Back to login
                </Button>
              </Box>
            </>
          ) : (
            <>
              {!isLogin && (
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <TextInput
                      register={register}
                      id="name"
                      placeholder="Full name"
                      type="text"
                      rules={{ required: "Name is required", maxLength: { value: 35, message: "Max 35 characters" } }}
                      errors={errors.name}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextInput
                      register={register}
                      id="phone"
                      placeholder="Phone number"
                      type="tel"
                      rules={{
                        required: "Phone is required",
                        pattern: { value: /^[0-9]{11}$/, message: "Enter a valid 11-digit number" },
                      }}
                      errors={errors.phone}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextInput
                      register={register}
                      id="email"
                      placeholder="Email address"
                      type="email"
                      rules={{
                        required: "Email is required",
                        pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Enter a valid email" },
                      }}
                      errors={errors.email}
                    />
                  </Grid>
                </Grid>
              )}

              <Grid container spacing={1} sx={{ mt: 1 }}>
                {isLogin && (
                  <Grid item xs={12}>
                    <TextInput
                      register={register}
                      id="identifier"
                      placeholder="Email or phone number"
                      type="text"
                      rules={{
                        required: "Email or phone number is required",
                        validate: (val) => {
                          const isPhone = /^[0-9]{11}$/.test(val);
                          const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
                          return isPhone || isEmail || "Enter a valid email or 11-digit phone number";
                        },
                      }}
                      errors={errors.identifier}
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextInputPassword
                    register={register}
                    id="password"
                    placeholder="Password"
                    type={showPassword ? "text" : "password"}
                    rules={{ required: "Password is required", minLength: { value: 6, message: "Minimum 6 characters" } }}
                    errors={errors.password}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowPassword((v) => !v)}>
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </Grid>

                {!isLogin && (
                  <Grid item xs={12}>
                    <TextInputPassword
                      register={register}
                      id="confirmPassword"
                      placeholder="Confirm password"
                      type={showPassword ? "text" : "password"}
                      rules={{
                        required: "Confirm password",
                        validate: (val) => val === watchedPassword || "Passwords do not match",
                      }}
                      errors={errors.confirmPassword}
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowPassword((v) => !v)}>
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      }
                    />
                  </Grid>
                )}
              </Grid>

              {isUserExist && !isLogin && (
                <Typography sx={{ color: tokens.color.error, textAlign: "center", mt: 1, fontSize: "0.78rem" }}>
                  This number is already registered. Try another number or login.
                </Typography>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || (isUserExist && !isLogin)}
                sx={{ ...primaryButtonSx, mt: 2 }}
              >
                {loading ? (isLogin ? "Logging in..." : "Registering...") : isLogin ? "Login" : "Register"}
              </Button>

              {isLogin && (
                <Box sx={{ textAlign: "center", mt: 1 }}>
                  <Button
                    size="small"
                    sx={linkButtonSx}
                    onClick={() => {
                      setIsForgotPassword(true);
                      setForgotStep(1);
                      reset();
                    }}
                  >
                    Forgot password?
                  </Button>
                </Box>
              )}

              <Typography sx={{ textAlign: "center", mt: 2, fontSize: "0.8rem", color: tokens.color.inkMuted }}>
                {isLogin ? "Don't have an account?" : "Already have an account?"}{" "}
                <Button
                  size="small"
                  sx={{ ...linkButtonSx, fontWeight: 700 }}
                  onClick={() => {
                    setIsLogin(!isLogin);
                    reset();
                  }}
                >
                  {isLogin ? "Register now" : "Login instead"}
                </Button>
              </Typography>
            </>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default LoginSignUpFormMini;